import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Home, Sparkles, Wallet, FileText, UserPlus } from 'lucide-react';
import { AuthContext } from '../App';
import api from '../utils/api';
import KidRegistrationWaiverModal from './KidRegistrationWaiverModal';
import AnalyticsTab from './AnalyticsTab';
import Payment from './Payment';
import ProfilePicModal from './ProfilePicModal';
import WorkspaceSidebar from './WorkspaceSidebar';
import { isFeatureEnabled } from '../utils/featureFlags';
import logo from '../assets/logo.jpeg';
import './ParentDashboard.css';

/* ─── Inline Radar Chart Component (SVG) ─── */
const RadarChart = ({ areas, size = 240 }) => {
  const center = size / 2;
  const radius = size / 2 - 50;
  const maxScore = 10;
  const numAreas = areas.length;
  if (numAreas === 0) return null;

  const getPoint = (index, score) => {
    const angle = (index * 360 / numAreas - 90) * Math.PI / 180;
    const r = (score / maxScore) * radius;
    return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
  };

  const gridLevels = [2, 4, 6, 8, 10];
  const points = areas.map((a, i) => getPoint(i, a.avg_score || 0));
  const pointsStr = points.map(p => `${p.x},${p.y}`).join(' ');
  const targetPoints = areas.map((a, i) => getPoint(i, a.target_score || 7));
  const targetStr = targetPoints.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', maxWidth: size, height: 'auto' }}>
      <defs>
        <linearGradient id="radarFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.25" />
        </linearGradient>
      </defs>
      {/* Grid polygons */}
      {gridLevels.map(level => {
        const gp = areas.map((_, i) => getPoint(i, level));
        return <polygon key={level} points={gp.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#e2e8f0" strokeWidth="1" opacity="0.5" />;
      })}
      {/* Axes */}
      {areas.map((_, i) => {
        const p = getPoint(i, maxScore);
        return <line key={i} x1={center} y1={center} x2={p.x} y2={p.y} stroke="#e2e8f0" strokeWidth="1" opacity="0.5" />;
      })}
      {/* Target polygon */}
      <polygon points={targetStr} fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4,3" opacity="0.6" />
      {/* Data polygon */}
      <polygon points={pointsStr} fill="url(#radarFill)" stroke="#00d4ff" strokeWidth="2.5" />
      {/* Data dots */}
      {points.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="4" fill="#00d4ff" stroke="white" strokeWidth="2" />)}
      {/* Labels with scores */}
      {areas.map((area, i) => {
        const lp = getPoint(i, maxScore + 2.5);
        const label = (area.area_name || '').length > 10 ? area.area_name.substring(0, 8) + '..' : area.area_name;
        const score = area.avg_score > 0 ? area.avg_score.toFixed(1) : '—';
        return (
          <g key={i}>
            <text x={lp.x} y={lp.y - 7} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="#64748b" fontWeight="600">{label}</text>
            <text x={lp.x} y={lp.y + 7} textAnchor="middle" dominantBaseline="middle" fontSize="11" fill="#00d4ff" fontWeight="700">{score}</text>
          </g>
        );
      })}
    </svg>
  );
};

/* ─── Milestone Generator ─── */
const generateMilestones = (assessments, player) => {
  const milestones = [];

  if (player.created_at) {
    milestones.push({ date: player.created_at, type: 'registration', title: 'Player Registered', description: `${player.first_name} joined MSKICA`, icon: '🏏' });
  }
  if (player.category_name) {
    milestones.push({ date: player.created_at || new Date().toISOString(), type: 'category', title: `${player.category_name} Category`, description: `Assigned to ${player.category_name} skill level`, icon: '🎯' });
  }

  const sorted = [...assessments].sort((a, b) => new Date(a.assessment_date) - new Date(b.assessment_date));
  if (sorted.length > 0) {
    milestones.push({ date: sorted[0].assessment_date, type: 'first', title: 'First Assessment', description: `Overall: ${sorted[0].overall_average?.toFixed(1) || 'N/A'}`, icon: '📊' });

    for (let i = 1; i < sorted.length; i++) {
      const cur = sorted[i].overall_average || 0;
      const prev = sorted[i - 1].overall_average || 0;
      if (cur > prev + 0.5) {
        milestones.push({ date: sorted[i].assessment_date, type: 'improvement', title: 'Score Improvement', description: `Overall improved from ${prev.toFixed(1)} to ${cur.toFixed(1)}`, icon: '📈' });
      }
      // High individual scores
      if (sorted[i].performance_areas) {
        sorted[i].performance_areas.forEach(area => {
          if (area.avg_score >= 9.0) {
            milestones.push({ date: sorted[i].assessment_date, type: 'excellence', title: `${area.area_name} Excellence`, description: `Scored ${area.avg_score.toFixed(1)}/10`, icon: '⭐' });
          }
        });
      }
    }
    if (sorted.length > 1) {
      const latest = sorted[sorted.length - 1];
      milestones.push({ date: latest.assessment_date, type: 'latest', title: 'Latest Assessment', description: `Overall: ${latest.overall_average?.toFixed(1) || 'N/A'}`, icon: '🏆' });
    }
  }
  milestones.sort((a, b) => new Date(b.date) - new Date(a.date));
  return milestones;
};

/* ─── Main Component ─── */
const ParentDashboard = () => {
  const { user, login, logout } = useContext(AuthContext);
  const location = useLocation();
  const resolveProfilePicUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    if (!base) return url;
    return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
  };
  const [activeTab, setActiveTab] = useState('dashboard');
  const [kids, setKids] = useState([]);
  const [allKids, setAllKids] = useState([]);
  const [skillCategories, setSkillCategories] = useState([]);
  const isAnalyticsEnabled = isFeatureEnabled('ANALYTICS_DASHBOARD', user);
  const isWalletEnabled = isFeatureEnabled('WALLET_TAB', user);
  const [selectedKid, setSelectedKid] = useState(null);
  const [kidAssessments, setKidAssessments] = useState([]);
  const [expandedAssessments, setExpandedAssessments] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAreaDetail, setSelectedAreaDetail] = useState(null);
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [pendingKidData, setPendingKidData] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', phone: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const [uploadingPic, setUploadingPic] = useState(false);
  const profilePicInputRef = React.useRef(null);
  const [newKid, setNewKid] = useState({ first_name: '', last_name: '', date_of_birth: '', gender: '', skill_category_id: '' });
  const [birthCertificate, setBirthCertificate] = useState(null);
  const [waivers, setWaivers] = useState([]);
  const [waiversLoading, setWaiversLoading] = useState(false);

  // NEW: Player profile view state
  const [selectedPlayerProfile, setSelectedPlayerProfile] = useState(null);
  const [isEditingKidIds, setIsEditingKidIds] = useState(null);
  const [kidIdsForm, setKidIdsForm] = useState({ cric_club_id: '', usacid: '' });
  const [kidIdsSaving, setKidIdsSaving] = useState(false);
  const [uploadingKidPic, setUploadingKidPic] = useState(false);
  const kidPicInputRef = React.useRef(null);
  const [picModalOpen, setPicModalOpen] = useState(false);
  const [picModalTarget, setPicModalTarget] = useState(null); // 'parent' | kidId

  useEffect(() => { fetchData(); fetchProfilePic(); }, []);

  useEffect(() => {
    const state = location.state || {};
    if (state.activeTab) {
      setActiveTab(state.activeTab);
    }
  }, [location.state]);

  /* ─── API Functions (preserved from original) ─── */
  const fetchProfilePic = async () => {
    try {
      const response = await api.get(`/api/profile-pic/${user.id}`);
      if (response.data.profile_pic_url) setProfilePicUrl(response.data.profile_pic_url);
    } catch (err) { console.log('No profile pic found:', err.message); }
  };

  const handleProfilePicUpload = async (file) => {
    if (!file) return;
    try {
      setUploadingPic(true); setError('');
      const formData = new FormData();
      formData.append('profile_pic', file);
      const response = await api.post('/api/profile-pic', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfilePicUrl(response.data.profile_pic_url);
      setSuccessMessage('Profile picture updated!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setPicModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setUploadingPic(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true); setError('');
      const [kidsResponse, categoriesResponse, goalsResponse] = await Promise.all([
        api.get('/api/players-with-assessments'),
        api.get('/api/skill-categories'),
        api.get('/api/category-goals')
      ]);
      const allPlayersData = kidsResponse.data.players || [];
      const categoryGoals = goalsResponse.data.goals || [];

      const allParentKids = allPlayersData.map(kid => ({ ...kid, status: kid.is_approved ? 'approved' : 'pending' }));
      const parentApprovedKids = allParentKids.filter(kid => kid.is_approved);
      allParentKids.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      const kidsWithAssessments = parentApprovedKids.map(player => {
        const playerCategoryGoals = categoryGoals.filter(g => g.skill_category_id === player.skill_category_id);
        const performanceAreasArray = [];
        if (player.performance_areas && typeof player.performance_areas === 'object') {
          Object.entries(player.performance_areas).forEach(([areaName, areaData]) => {
            let areaGoal = playerCategoryGoals.find(g => g.area_name && g.area_name.toLowerCase() === areaName.toLowerCase());
            if (!areaGoal) areaGoal = playerCategoryGoals.find(g => g.area_name && g.area_name.trim().toLowerCase() === areaName.trim().toLowerCase());
            if (!areaGoal) areaGoal = playerCategoryGoals.find(g => g.area_name && (g.area_name.toLowerCase().includes(areaName.toLowerCase()) || areaName.toLowerCase().includes(g.area_name.toLowerCase())));

            const defaultTargets = { 'Batting': 7.0, 'Bowling': 7.0, 'Fielding': 7.0, 'Fitness': 7.5, 'Attitude': 8.0, 'General': 7.0, 'wicketkeeping': 7.0, 'Wicketkeeping': 7.0, 'wicket keeping': 7.0, 'Wicket keeping': 7.0 };
            const finalTargetScore = areaGoal?.target_score || areaData.target_score || defaultTargets[areaName] || 7.0;

            performanceAreasArray.push({
              area_name: areaName,
              avg_score: areaData.current_avg || 0,
              trend: areaData.trend || 'neutral',
              previous_avg: areaData.previous_avg,
              trend_difference: areaData.trend_difference,
              target_score: finalTargetScore,
              goal_achievement: areaData.goal_achievement || 0
            });
          });
        }

        const categoryProgress = player.overall_goal_achievement || 0;
        const avgTargetScore = playerCategoryGoals.length > 0 ? playerCategoryGoals.reduce((s, g) => s + g.target_score, 0) / playerCategoryGoals.length : 0;
        const skillTarget = avgTargetScore > 0 ? `${avgTargetScore.toFixed(1)}/10 avg` : 'Not set';

        return {
          id: player.id, first_name: player.first_name, last_name: player.last_name,
          date_of_birth: player.date_of_birth || player.dateOfBirth, gender: player.gender,
          skill_category_id: player.skill_category_id, performance_level: player.performance_level,
          category_name: player.skill_category_name || player.category_name, color_code: player.color_code,
          assessments_count: player.latest_assessment_date ? 1 : 0,
          performance_areas: performanceAreasArray,
          latest_assessment_date: player.latest_assessment_date,
          overall_average: typeof player.overall_average === 'number' ? player.overall_average : parseFloat(player.overall_average) || 0,
          goal_achievement_percentage: player.overall_goal_achievement,
          created_at: player.created_at,
          skill_target: skillTarget, category_progress: Math.min(categoryProgress, 100),
          category_goals: playerCategoryGoals,
          // NEW fields
          profile_pic_url: player.profile_pic_url,
          cric_club_id: player.cric_club_id,
          usacid: player.usacid,
          primary_role: player.primary_role,
          batting_order: player.batting_order,
          bowling_order: player.bowling_order
        };
      });

      setKids(kidsWithAssessments);
      setAllKids(allParentKids);
      setSkillCategories(categoriesResponse.data.categories || []);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to load data');
    } finally { setLoading(false); }
  };

  const startEditingProfile = () => {
    setProfileForm({
      first_name: user.first_name || user.firstName || '',
      last_name: user.last_name || user.lastName || '',
      phone: user.phone || ''
    });
    setIsEditingProfile(true);
  };
  const cancelEditingProfile = () => { setIsEditingProfile(false); setProfileForm({ first_name: '', last_name: '', phone: '' }); };

  const handleProfileSave = async () => {
    if (!profileForm.first_name.trim() || !profileForm.last_name.trim()) { setError('First name and last name are required'); return; }
    try {
      setProfileSaving(true); setError('');
      const response = await api.put('/api/update-profile', {
        first_name: profileForm.first_name.trim(), last_name: profileForm.last_name.trim(), phone: profileForm.phone.trim()
      });
      const updatedUser = response.data.user;
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const merged = { ...currentUser, ...updatedUser };
      localStorage.setItem('user', JSON.stringify(merged));
      login(merged, localStorage.getItem('token'));
      setSuccessMessage('Profile updated successfully!'); setIsEditingProfile(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Failed to update profile'); }
    finally { setProfileSaving(false); }
  };

  const handleAddKid = async (e) => {
    e.preventDefault();
    if (!newKid.first_name || !newKid.last_name || !newKid.date_of_birth || !newKid.skill_category_id) { setError('Please fill in all required fields'); return; }
    setError(''); setSuccessMessage('');
    setPendingKidData(newKid);
    setShowWaiverModal(true);
  };

  const handleWaiverComplete = async (waiverData) => {
    try {
      setError(''); setSuccessMessage('');
      await api.post('/api/add_kid_with_waiver', waiverData, { headers: { 'Content-Type': 'application/json' } });
      setSuccessMessage('Registration submitted successfully! Your child\'s registration is pending approval.');
      setNewKid({ first_name: '', last_name: '', date_of_birth: '', gender: '', skill_category_id: '' });
      setBirthCertificate(null); setPendingKidData(null); setShowWaiverModal(false);
      setActiveTab('dashboard'); fetchData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add child with waiver');
      setShowWaiverModal(false);
    }
  };
  const handleWaiverCancel = () => { setShowWaiverModal(false); setPendingKidData(null); };

  const startEditingKidIds = (kid) => {
    setKidIdsForm({ cric_club_id: kid.cric_club_id || '', usacid: kid.usacid || '' });
    setIsEditingKidIds(kid.id);
  };
  const cancelEditingKidIds = () => { setIsEditingKidIds(null); };
  const handleSaveKidIds = async (kidId) => {
    try {
      setKidIdsSaving(true); setError('');
      await api.put('/api/update-kid-details', {
        kid_id: kidId,
        cric_club_id: kidIdsForm.cric_club_id.trim() || null,
        usacid: kidIdsForm.usacid.trim() || null
      });
      setSuccessMessage('Player IDs updated successfully!');
      setIsEditingKidIds(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      await fetchData();
      // Update selectedPlayerProfile with new values
      setSelectedPlayerProfile(prev => prev ? { ...prev, cric_club_id: kidIdsForm.cric_club_id.trim() || null, usacid: kidIdsForm.usacid.trim() || null } : null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update player IDs');
    } finally { setKidIdsSaving(false); }
  };

  const handleKidProfilePicUpload = async (file, kidId) => {
    if (!file) return;
    try {
      setUploadingKidPic(true); setError('');
      const formData = new FormData();
      formData.append('profile_pic', file);
      const response = await api.post(`/api/kid-profile-pic?kid_id=${kidId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSelectedPlayerProfile(prev => prev ? { ...prev, profile_pic_url: response.data.profile_pic_url } : null);
      setSuccessMessage('Player profile picture updated!');
      setTimeout(() => setSuccessMessage(''), 3000);
      await fetchData();
      setPicModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setUploadingKidPic(false);
    }
  };

  const fetchWaivers = async () => {
    try { setWaiversLoading(true); const r = await api.get(`/api/waivers/${user.id}`); setWaivers(r.data.waivers || []); }
    catch (err) { console.error('Failed to fetch waivers:', err); }
    finally { setWaiversLoading(false); }
  };

  const fetchKidAssessments = async (kidId) => {
    try {
      setError('');
      const [assessmentsResponse, goalsResponse] = await Promise.all([
        api.get(`/api/kid-assessments/${kidId}`), api.get('/api/category-goals')
      ]);
      const assessments = assessmentsResponse.data.assessments || [];
      const categoryGoals = goalsResponse.data.goals || [];

      const processedAssessments = assessments.map(assessment => {
        const areaScores = {};
        assessment.scores.forEach(score => {
          if (!areaScores[score.area_name]) areaScores[score.area_name] = { scores: [], total: 0, count: 0 };
          areaScores[score.area_name].scores.push(score);
          areaScores[score.area_name].total += score.score;
          areaScores[score.area_name].count += 1;
        });

        const performanceAreas = Object.entries(areaScores).map(([areaName, data]) => {
          const avgScore = data.total / data.count;
          const kid = kids.find(k => k.id === kidId);
          const areaGoal = categoryGoals.find(g => g.skill_category_id === kid?.skill_category_id && g.performance_area_name === areaName);
          const targetScore = areaGoal?.target_score || 0;
          return { area_name: areaName, avg_score: Math.round(avgScore * 10) / 10, target_score: targetScore, goal_achievement: targetScore > 0 ? Math.round((avgScore / targetScore) * 100) : 0, scores: data.scores };
        });

        const areasForAverage = performanceAreas.filter(area => {
          const n = area.area_name.toLowerCase().trim();
          if ((area.avg_score || 0) === 0) return false;
          const hasBowl = performanceAreas.some(a => a.area_name.toLowerCase().includes('bowl') && a.avg_score > 0);
          const hasWK = performanceAreas.some(a => (a.area_name.toLowerCase().includes('wicket') || a.area_name.toLowerCase().includes('keeper')) && a.avg_score > 0);
          if (hasBowl && hasWK && n.includes('bowl')) return false;
          return true;
        });
        const overallAverage = areasForAverage.length > 0 ? areasForAverage.reduce((s, a) => s + a.avg_score, 0) / areasForAverage.length : 0;

        return { ...assessment, performance_areas: performanceAreas, overall_average: Math.round(overallAverage * 10) / 10, area_count: performanceAreas.length, areas_in_average: areasForAverage.length };
      });

      setKidAssessments(processedAssessments.sort((a, b) => new Date(b.assessment_date) - new Date(a.assessment_date)).slice(0, 10));
    } catch (error) { setError('Failed to load assessments'); }
  };

  /* ─── Helper Functions ─── */
  const getAreaIcon = (areaName) => {
    const icons = { 'batting': '🏏', 'bowling': '🎯', 'fielding': '🥎', 'wicket keeping': '🧤', 'wicketkeeping': '🧤', 'wicket-keeping': '🧤', 'Wicketkeeping': '🧤', 'fitness': '💪', 'attitude': '😊', 'teamwork': '🤝', 'technique': '⚙️', 'strategy': '🧠' };
    return icons[(areaName || '').toLowerCase().trim()] || '🏏';
  };
  const formatScore = (score) => { const n = parseFloat(score); return isNaN(n) ? '0.0' : n.toFixed(1); };
  const getScoreClass = (score) => { if (score >= 8) return 'excellent'; if (score >= 6) return 'good'; if (score >= 4) return 'average'; if (score >= 2) return 'below'; return 'poor'; };
  const getFillClass = (score) => `pd-fill-${getScoreClass(score)}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';
  const calculateAge = (dob) => { if (!dob) return 'N/A'; const t = new Date(); const b = new Date(dob); let a = t.getFullYear() - b.getFullYear(); if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--; return a; };

  const toggleAssessmentDetails = (id) => {
    setExpandedAssessments(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const openAreaDetailModal = (areaData, assessment) => {
    setSelectedAreaDetail({ ...areaData, assessment_date: assessment.assessment_date, kid_name: selectedKid ? `${selectedKid.first_name} ${selectedKid.last_name}` : '', area_comments: assessment.area_comments, assessment_id: assessment.id });
    setShowDetailModal(true);
  };
  const closeAreaDetailModal = () => { setShowDetailModal(false); setSelectedAreaDetail(null); };

  const openPlayerProfile = (kid) => {
    const approvedKid = kids.find(k => k.id === kid.id);
    if (approvedKid) {
      setSelectedKid(approvedKid);
      setSelectedPlayerProfile(approvedKid);
      fetchKidAssessments(approvedKid.id);
    }
  };
  const closePlayerProfile = () => {
    setSelectedPlayerProfile(null);
    setIsEditingKidIds(null);
  };

  /* ─── Render ─── */
  if (loading) {
    return (
      <div className="pd-container">
        <div className="pd-loading">
          <div className="pd-loading-spinner"></div>
          <div className="pd-loading-text">Loading your dashboard...</div>
        </div>
      </div>
    );
  }

  const sidebarItems = [
    { key: 'dashboard', label: 'Dashboard', icon: Home },
    ...(isAnalyticsEnabled ? [{ key: 'analytics', label: 'Cric AI', icon: Sparkles }] : []),
    ...(isWalletEnabled ? [{ key: 'wallet', label: 'Payment', icon: Wallet }] : []),
    { key: 'waivers', label: 'Waivers', icon: FileText },
    { key: 'register', label: 'Register Player', icon: UserPlus }
  ];

  return (
    <div className="pd-container pd-workspace-shell">
      <WorkspaceSidebar
        showBrand={false}
        brandTitle={process.env.REACT_APP_ACADEMY_NAME || 'MSKICA'}
        brandSubtitle="Player workspace"
        items={sidebarItems}
        activeKey={activeTab}
        onSelect={(key) => { setActiveTab(key); setSelectedPlayerProfile(null); if (key === 'waivers') fetchWaivers(); }}
        userName={user.first_name || user.email}
        userRole={user.user_type || 'parent'}
        userAvatarUrl={resolveProfilePicUrl(profilePicUrl)}
        onLogout={logout}
      />
      <div className="pd-workspace-content">
      {/* Header */}
      <header className="pd-header">
        <div className="pd-header-inner">
          <div className="pd-header-brand">
            <div className="pd-header-logo">🏏</div>
            <div>
              <h1 className="pd-header-title">Welcome, {user.first_name || user.email}</h1>
              <p className="pd-header-subtitle">Account Dashboard</p>
            </div>
          </div>
          <div className="pd-header-academy">
            <div>
              <span className="pd-header-academy-name">{process.env.REACT_APP_ACADEMY_NAME || 'MSK PRASAD\'s International Cricket Academy - Seattle'}</span>
              <span className="pd-header-academy-tagline">{process.env.REACT_APP_ACADEMY_TAGLINE || 'Performance Excellence'}</span>
            </div>
            <img
              src={logo}
              alt="Academy logo"
              className="pd-header-academy-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>
      </header>

      {/* Alerts */}
      {error && <div className="pd-error"><span>⚠️</span> {error} <button className="pd-error-close" onClick={() => setError('')}>×</button></div>}
      {successMessage && <div className="pd-success"><span>✅</span> {successMessage}</div>}

      {/* Main Content */}
      <main className="pd-main">

        {/* ═══════ PLAYER PROFILE VIEW (ESPN Cricinfo style) ═══════ */}
        {selectedPlayerProfile && activeTab === 'dashboard' && (() => {
          const kid = selectedPlayerProfile;
          const milestones = generateMilestones(kidAssessments, kid);
          const totalAssessments = kidAssessments.length;
          const bestScore = kidAssessments.length > 0 ? Math.max(...kidAssessments.map(a => a.overall_average || 0)) : 0;
          const areasCount = kid.performance_areas?.length || 0;

          return (
            <div className="pd-player-profile">
              <button className="pd-back-btn" onClick={closePlayerProfile}>← Back to Dashboard</button>

              {/* Hero */}
              <div className="pd-pp-hero">
                <div className="pd-pp-hero-inner">
                  <div className="pd-pp-avatar-wrap" onClick={() => { setPicModalTarget(kid.id); setPicModalOpen(true); }}>
                    {kid.profile_pic_url ? (
                      <img src={resolveProfilePicUrl(kid.profile_pic_url)} alt={kid.first_name} className="pd-pp-avatar" />
                    ) : (
                      <div className="pd-pp-avatar-placeholder">{(kid.first_name || '?')[0]}{(kid.last_name || '?')[0]}</div>
                    )}
                    <div className="pd-pp-avatar-overlay">{uploadingKidPic ? '...' : '📷'}</div>
                  </div>
                  <div className="pd-pp-info">
                    <h2 className="pd-pp-name">{kid.first_name} {kid.last_name}</h2>
                    <div className="pd-pp-id-row">
                      {isEditingKidIds === kid.id ? (
                        <div className="pd-pp-id-edit-form">
                          <div className="pd-pp-id-edit-field">
                            <label className="pd-pp-id-edit-label">Cric Club ID</label>
                            <input
                              className="pd-pp-id-edit-input"
                              type="text"
                              value={kidIdsForm.cric_club_id}
                              onChange={(e) => setKidIdsForm({ ...kidIdsForm, cric_club_id: e.target.value })}
                              placeholder="Enter Cric Club ID"
                              maxLength={50}
                              disabled={kidIdsSaving}
                            />
                          </div>
                          <div className="pd-pp-id-edit-field">
                            <label className="pd-pp-id-edit-label">USA Cricket ID</label>
                            <input
                              className="pd-pp-id-edit-input"
                              type="text"
                              value={kidIdsForm.usacid}
                              onChange={(e) => setKidIdsForm({ ...kidIdsForm, usacid: e.target.value })}
                              placeholder="Enter USA Cricket ID"
                              maxLength={50}
                              disabled={kidIdsSaving}
                            />
                          </div>
                          <div className="pd-pp-id-edit-actions">
                            <button className="pd-pp-id-save-btn" onClick={() => handleSaveKidIds(kid.id)} disabled={kidIdsSaving}>
                              {kidIdsSaving ? '⏳' : '✓'} {kidIdsSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button className="pd-pp-id-cancel-btn" onClick={cancelEditingKidIds} disabled={kidIdsSaving}>✕ Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {kid.cric_club_id && (
                            <div className="pd-pp-id-item">
                              <span className="pd-pp-id-label">Cric Club ID</span>
                              <span className="pd-pp-id-value">{kid.cric_club_id}</span>
                            </div>
                          )}
                          {kid.usacid && (
                            <div className="pd-pp-id-item">
                              <span className="pd-pp-id-label">USA Cricket ID</span>
                              <span className="pd-pp-id-value">{kid.usacid}</span>
                            </div>
                          )}
                          <button className="pd-pp-id-edit-btn" onClick={() => startEditingKidIds(kid)} title="Edit IDs">
                            ✏️ {(!kid.cric_club_id && !kid.usacid) ? 'Add IDs' : 'Edit'}
                          </button>
                        </>
                      )}
                    </div>
                    <div className="pd-pp-tags">
                      {kid.category_name && <span className="pd-pp-tag pd-tag-category">{kid.category_name}</span>}
                      {kid.primary_role && <span className="pd-pp-tag pd-tag-role">{kid.primary_role}</span>}
                    </div>
                    <div className="pd-pp-meta-row">
                      <span className="pd-pp-meta-item"><span>🎂</span> Age: {calculateAge(kid.date_of_birth || kid.dateOfBirth)}</span>
                      {kid.batting_order && <span className="pd-pp-meta-item"><span>🏏</span> Bat #{kid.batting_order}</span>}
                      {kid.bowling_order && <span className="pd-pp-meta-item"><span>🎯</span> Bowl #{kid.bowling_order}</span>}
                      <span className="pd-pp-meta-item"><span>📅</span> Since {formatDate(kid.created_at)}</span>
                    </div>
                  </div>
                  <div className="pd-pp-score-display">
                    <div className="pd-pp-score-circle">
                      <span className="pd-pp-score-number">{kid.overall_average ? formatScore(kid.overall_average) : '—'}</span>
                      <span className="pd-pp-score-sub">Overall</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Row */}
              <div className="pd-pp-stats-row">
                <div className="pd-pp-stat"><div className="pd-pp-stat-icon">📊</div><div className="pd-pp-stat-value">{totalAssessments}</div><div className="pd-pp-stat-label">Assessments</div></div>
                <div className="pd-pp-stat"><div className="pd-pp-stat-icon">🏆</div><div className="pd-pp-stat-value">{bestScore > 0 ? formatScore(bestScore) : '—'}</div><div className="pd-pp-stat-label">Best Score</div></div>
                <div className="pd-pp-stat"><div className="pd-pp-stat-icon">📈</div><div className="pd-pp-stat-value">{areasCount}</div><div className="pd-pp-stat-label">Skill Areas</div></div>
                <div className="pd-pp-stat"><div className="pd-pp-stat-icon">🎯</div><div className="pd-pp-stat-value">{kid.category_progress ? `${Math.round(kid.category_progress)}%` : '—'}</div><div className="pd-pp-stat-label">Goal Progress</div></div>
              </div>

              {/* Radar + Performance Bars */}
              {kid.performance_areas && kid.performance_areas.length > 0 && (
                <div className="pd-pp-content">
                  <div className="pd-glass-card">
                    <h3 className="pd-glass-card-title"><span>📡</span> Performance Radar</h3>
                    <div className="pd-radar-wrap">
                      <RadarChart areas={kid.performance_areas} size={280} />
                    </div>
                  </div>
                  <div className="pd-glass-card">
                    <h3 className="pd-glass-card-title"><span>📊</span> Skill Breakdown</h3>
                    <div className="pd-perf-bars">
                      {kid.performance_areas.map((area, i) => (
                        <div key={i} className="pd-perf-bar-item">
                          <div className="pd-perf-bar-icon">{getAreaIcon(area.area_name)}</div>
                          <div className="pd-perf-bar-info">
                            <div className="pd-perf-bar-header">
                              <span className="pd-perf-bar-name">{area.area_name}</span>
                              <span className="pd-perf-bar-score">{formatScore(area.avg_score)} / {formatScore(area.target_score)}</span>
                            </div>
                            <div className="pd-perf-bar-track">
                              <div className={`pd-perf-bar-fill ${getScoreClass(area.avg_score)}`} style={{ width: `${(area.avg_score / 10) * 100}%` }}></div>
                              {area.target_score > 0 && <div className="pd-perf-bar-target" style={{ left: `${(area.target_score / 10) * 100}%` }}></div>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Assessment History Timeline */}
              <div className="pd-glass-card" style={{ marginBottom: 32 }}>
                <h3 className="pd-glass-card-title"><span>📅</span> Assessment History</h3>
                {kidAssessments.length === 0 ? (
                  <div className="pd-no-data"><span className="pd-no-data-icon">📊</span>No assessments found yet</div>
                ) : (
                  <div className="pd-timeline">
                    {kidAssessments.map(assessment => {
                      const isExpanded = expandedAssessments.has(assessment.id);
                      return (
                        <div key={assessment.id} className="pd-timeline-item">
                          <div className="pd-timeline-dot"></div>
                          <div className={`pd-timeline-card ${isExpanded ? 'expanded' : ''}`} onClick={() => toggleAssessmentDetails(assessment.id)}>
                            <div className="pd-timeline-header">
                              <span className="pd-timeline-date"><span>📅</span> {formatDate(assessment.assessment_date)}</span>
                              <span className={`pd-timeline-score ${getScoreClass(assessment.overall_average)}`}>
                                {assessment.overall_average > 0 ? formatScore(assessment.overall_average) : 'N/A'}
                              </span>
                            </div>
                            <div className="pd-timeline-areas">
                              {assessment.performance_areas?.map((area, i) => (
                                <div key={i} className="pd-timeline-area" onClick={(e) => { e.stopPropagation(); if (area.avg_score > 0) openAreaDetailModal(area, assessment); }}>
                                  <div className="pd-timeline-area-icon">{getAreaIcon(area.area_name)}</div>
                                  <div className="pd-timeline-area-name">{area.area_name}</div>
                                  <div className={`pd-timeline-area-score`} style={{ color: area.avg_score >= 8 ? '#059669' : area.avg_score >= 6 ? '#0891b2' : area.avg_score >= 4 ? '#d97706' : '#dc2626' }}>
                                    {area.avg_score > 0 ? formatScore(area.avg_score) : 'N/A'}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="pd-timeline-expand">{isExpanded ? '▲ Collapse' : '▼ View details'}</div>
                            {isExpanded && (
                              <div className="pd-timeline-details">
                                {assessment.overall_comments && (
                                  <div className="pd-timeline-comments"><strong>Coach Comments:</strong> {assessment.overall_comments}</div>
                                )}
                                {assessment.area_comments && Object.entries(assessment.area_comments).map(([area, comment]) => (
                                  comment && <div key={area} className="pd-timeline-comments"><strong>{area}:</strong> {comment}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Milestones Timeline */}
              {milestones.length > 0 && (
                <div className="pd-glass-card">
                  <h3 className="pd-glass-card-title"><span>🏅</span> Milestones</h3>
                  <div className="pd-milestones">
                    {milestones.map((m, i) => (
                      <div key={i} className="pd-milestone-item">
                        <div className="pd-milestone-icon">{m.icon}</div>
                        <div className="pd-milestone-content">
                          <div className="pd-milestone-date">{formatDate(m.date)}</div>
                          <div className="pd-milestone-title">{m.title}</div>
                          <div className="pd-milestone-desc">{m.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ═══════ DASHBOARD TAB ═══════ */}
        {activeTab === 'dashboard' && !selectedPlayerProfile && (
          <div>
            {/* Profile Hero */}
            <div className="pd-profile-hero">
              <div className="pd-profile-hero-inner">
                <div className="pd-profile-pic-wrap" onClick={() => { setPicModalTarget('parent'); setPicModalOpen(true); }}>
                  {profilePicUrl ? (
                    <img src={resolveProfilePicUrl(profilePicUrl)} alt="Profile" className="pd-profile-pic" />
                  ) : (
                    <div className="pd-profile-pic-placeholder">
                      {(user.first_name || user.firstName || '?')[0]}{(user.last_name || user.lastName || '?')[0]}
                    </div>
                  )}
                  <div className="pd-profile-pic-overlay">{uploadingPic ? '...' : '📷'}</div>
                </div>
                <div className="pd-profile-details">
                  {!isEditingProfile ? (
                    <>
                      <h2 className="pd-profile-name">{user.first_name || user.firstName} {user.last_name || user.lastName}</h2>
                      <div className="pd-profile-role">Account Admin</div>
                      <div className="pd-profile-meta">
                        <span className="pd-profile-meta-item"><span>📧</span> {user.email}</span>
                        {user.phone && <span className="pd-profile-meta-item"><span>📱</span> {user.phone}</span>}
                        <span className="pd-profile-meta-item"><span>📅</span> Member since {user.created_at ? formatDate(user.created_at) : 'N/A'}</span>
                        <span className="pd-profile-meta-item"><span>✅</span> {user.waiver_signed ? 'Waiver Signed' : 'Waiver Pending'}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="pd-profile-edit-row">
                        <div className="pd-profile-edit-group">
                          <label className="pd-profile-edit-label">First Name</label>
                          <input className="pd-profile-edit-input" value={profileForm.first_name} onChange={(e) => setProfileForm(p => ({ ...p, first_name: e.target.value }))} />
                        </div>
                        <div className="pd-profile-edit-group">
                          <label className="pd-profile-edit-label">Last Name</label>
                          <input className="pd-profile-edit-input" value={profileForm.last_name} onChange={(e) => setProfileForm(p => ({ ...p, last_name: e.target.value }))} />
                        </div>
                      </div>
                      <div className="pd-profile-edit-row">
                        <div className="pd-profile-edit-group">
                          <label className="pd-profile-edit-label">Phone</label>
                          <input className="pd-profile-edit-input" value={profileForm.phone} onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))} />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="pd-profile-actions">
                  {!isEditingProfile ? (
                    <button className="pd-edit-btn" onClick={startEditingProfile}><span>✏️</span> Edit Profile</button>
                  ) : (
                    <>
                      <button className="pd-save-btn" onClick={handleProfileSave} disabled={profileSaving}>{profileSaving ? 'Saving...' : '💾 Save'}</button>
                      <button className="pd-cancel-btn" onClick={cancelEditingProfile} disabled={profileSaving}>Cancel</button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="pd-stats-bar">
              <div className="pd-stat-card"><span className="pd-stat-icon">👥</span><div className="pd-stat-value">{allKids.length}</div><div className="pd-stat-label">Total Players</div></div>
              <div className="pd-stat-card"><span className="pd-stat-icon">✅</span><div className="pd-stat-value">{kids.length}</div><div className="pd-stat-label">Approved</div></div>
              <div className="pd-stat-card"><span className="pd-stat-icon">⏳</span><div className="pd-stat-value">{allKids.length - kids.length}</div><div className="pd-stat-label">Pending</div></div>
              <div className="pd-stat-card"><span className="pd-stat-icon">📊</span><div className="pd-stat-value">{kids.filter(k => k.performance_areas && k.performance_areas.length > 0).length}</div><div className="pd-stat-label">Assessed</div></div>
            </div>

            {/* Section Header */}
            <div className="pd-section-header">
              <div className="pd-section-title">
                <h2>My Players</h2>
                {allKids.length > 0 && <span className="pd-section-badge">{allKids.length}</span>}
              </div>
              <div className="pd-section-actions">
              </div>
            </div>

            {/* Players Grid */}
            {allKids.length === 0 ? (
              <div className="pd-empty">
                <span className="pd-empty-icon">🏏</span>
                <h3>No Players Registered</h3>
                <p>Start your cricket journey by registering your first player.</p>
                <button className="pd-btn pd-btn-accent" onClick={() => setActiveTab('register')}>🚀 Register First Player</button>
              </div>
            ) : (
              <div className="pd-players-grid">
                {allKids.map(kid => {
                  const approvedKid = kids.find(k => k.id === kid.id);
                  const isPending = kid.status === 'pending';
                  const overallAvg = approvedKid?.overall_average || 0;
                  const perfAreas = approvedKid?.performance_areas || [];

                  return (
                    <div key={kid.id} className={`pd-player-card ${isPending ? 'pending' : ''}`} onClick={() => !isPending && approvedKid && openPlayerProfile(kid)}>
                      {/* Card Hero */}
                      <div className="pd-player-card-hero">
                        {kid.profile_pic_url ? (
                          <img src={resolveProfilePicUrl(kid.profile_pic_url)} alt={kid.first_name} className="pd-player-avatar" />
                        ) : (
                          <div className="pd-player-avatar-placeholder">{(kid.first_name || '?')[0]}{(kid.last_name || '?')[0]}</div>
                        )}
                        <div className="pd-player-card-info">
                          <h3 className="pd-player-card-name">{kid.first_name} {kid.last_name}</h3>
                          <div className="pd-player-card-ids">
                            {isEditingKidIds === kid.id ? (
                              <div className="pd-card-inline-edit" onClick={(e) => e.stopPropagation()}>
                                <div className="pd-card-inline-fields">
                                  <div className="pd-card-inline-field">
                                    <span className="pd-id-label">CC</span>
                                    <input className="pd-card-inline-input" type="text" value={kidIdsForm.cric_club_id} onChange={(e) => setKidIdsForm({ ...kidIdsForm, cric_club_id: e.target.value })} placeholder="Cric Club ID" maxLength={50} disabled={kidIdsSaving} />
                                  </div>
                                  <div className="pd-card-inline-field">
                                    <span className="pd-id-label">USA</span>
                                    <input className="pd-card-inline-input" type="text" value={kidIdsForm.usacid} onChange={(e) => setKidIdsForm({ ...kidIdsForm, usacid: e.target.value })} placeholder="USA Cricket ID" maxLength={50} disabled={kidIdsSaving} />
                                  </div>
                                </div>
                                <div className="pd-card-inline-actions">
                                  <button className="pd-card-inline-save" onClick={() => handleSaveKidIds(kid.id)} disabled={kidIdsSaving}>{kidIdsSaving ? '⏳' : '✓'}</button>
                                  <button className="pd-card-inline-cancel" onClick={cancelEditingKidIds} disabled={kidIdsSaving}>✕</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {kid.cric_club_id && <span className="pd-id-badge"><span className="pd-id-label">CC</span> {kid.cric_club_id}</span>}
                                {kid.usacid && <span className="pd-id-badge"><span className="pd-id-label">USA</span> {kid.usacid}</span>}
                                {!isPending && (
                                  <button className="pd-id-edit-inline-btn" onClick={(e) => { e.stopPropagation(); startEditingKidIds(kid); }} title="Edit IDs">
                                    ✏️ {(!kid.cric_club_id && !kid.usacid) ? 'Add IDs' : 'Edit'}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          <div className="pd-player-card-meta">
                            {kid.skill_category_name && <span className="pd-player-tag pd-tag-category">{kid.skill_category_name}</span>}
                            {kid.primary_role && <span className="pd-player-tag pd-tag-role">{kid.primary_role}</span>}
                            <span className={`pd-player-tag ${isPending ? 'pd-tag-pending' : 'pd-tag-approved'}`}>{isPending ? '⏳ Pending' : '✓ Approved'}</span>
                          </div>
                        </div>
                        {!isPending && overallAvg > 0 && (
                          <div className="pd-player-overall-score">
                            <span className="pd-overall-value">{formatScore(overallAvg)}</span>
                            <span className="pd-overall-label">Overall</span>
                          </div>
                        )}
                      </div>

                      {/* Card Body - Mini Performance */}
                      <div className="pd-player-card-body">
                        {!isPending && perfAreas.length > 0 ? (
                          <div className="pd-mini-performance">
                            {perfAreas.slice(0, 6).map((area, i) => (
                              <div key={i} className="pd-mini-area">
                                <span className="pd-mini-area-icon">{getAreaIcon(area.area_name)}</span>
                                <div className="pd-mini-area-name">{area.area_name?.length > 12 ? area.area_name.substring(0, 10) + '..' : area.area_name}</div>
                                <div className="pd-mini-area-score">{area.avg_score > 0 ? formatScore(area.avg_score) : '—'}</div>
                                <div className="pd-mini-area-bar"><div className={`pd-mini-area-fill ${getFillClass(area.avg_score)}`} style={{ width: `${(area.avg_score / 10) * 100}%` }}></div></div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="pd-no-data" style={{ padding: '24px' }}>
                            <span style={{ fontSize: '2rem' }}>{isPending ? '⏳' : '📊'}</span>
                            <p style={{ margin: '8px 0 0', fontSize: '0.85rem' }}>{isPending ? 'Awaiting approval' : 'No assessments yet'}</p>
                          </div>
                        )}
                      </div>

                      {/* Card Footer */}
                      <div className="pd-player-card-footer">
                        <span className="pd-card-date"><span>📅</span> {approvedKid?.latest_assessment_date ? formatDate(approvedKid.latest_assessment_date) : 'No assessment'}</span>
                        {!isPending && approvedKid && (
                          <button className="pd-view-profile-btn" onClick={(e) => { e.stopPropagation(); openPlayerProfile(kid); }}>View Profile →</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══════ ANALYTICS TAB ═══════ */}
        {isAnalyticsEnabled && activeTab === 'analytics' && <AnalyticsTab kids={kids} user={user} />}

        {/* ═══════ PAYMENT TAB ═══════ */}
        {activeTab === 'wallet' && <Payment user={user} kids={kids} showOrders={location.state?.showOrders} />}

        {/* ═══════ WAIVERS TAB ═══════ */}
        {activeTab === 'waivers' && (
          <div>
            <div className="pd-section-header">
              <div className="pd-section-title"><h2>📋 Signed Waivers</h2></div>
            </div>
            {waiversLoading ? (
              <div className="pd-no-data"><div className="pd-loading-spinner" style={{ margin: '0 auto' }}></div><p>Loading waivers...</p></div>
            ) : waivers.length === 0 ? (
              <div className="pd-empty"><span className="pd-empty-icon">📝</span><h3>No Signed Waivers</h3><p>No signed waivers found.</p></div>
            ) : (
              <div className="pd-waivers-list">
                {waivers.map((waiver, index) => (
                  <div key={index} className="pd-waiver-item">
                    <div className="pd-waiver-info">
                      <div className="pd-waiver-icon">📄</div>
                      <div>
                        <div className="pd-waiver-title">Waiver - {waiver.display_date}</div>
                        <div className="pd-waiver-meta">Signed on {waiver.display_date}{waiver.size && ` • ${(waiver.size / 1024).toFixed(1)} KB`}</div>
                      </div>
                    </div>
                    <div className="pd-waiver-actions">
                      <button className="pd-waiver-btn pd-waiver-btn-view" onClick={async () => {
                        try { const res = await api.get(`/api/waiver-pdf/${user.id}/${waiver.date_folder}?mode=inline`, { responseType: 'blob' }); window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank'); }
                        catch (err) { console.error('Failed to view waiver:', err); }
                      }}>👁️ View</button>
                      <button className="pd-waiver-btn pd-waiver-btn-download" onClick={async () => {
                        try { const res = await api.get(`/api/waiver-pdf/${user.id}/${waiver.date_folder}?mode=download`, { responseType: 'blob' }); const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); a.download = `waiver_${waiver.date_folder}.pdf`; a.click(); }
                        catch (err) { console.error('Failed to download waiver:', err); }
                      }}>⬇️ Download</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══════ REGISTER TAB ═══════ */}
        {activeTab === 'register' && (
          <div className="pd-register">
            <div className="pd-register-header">
              <h2>➕ Register New Player</h2>
              <p>Fill in the details below to register a new player</p>
            </div>
            <div className="pd-form-card">
              <form onSubmit={handleAddKid}>
                <div className="pd-form-grid">
                  <div className="pd-form-group">
                    <label className="pd-form-label">First Name <span className="required">*</span></label>
                    <input className="pd-form-input" type="text" value={newKid.first_name} onChange={(e) => setNewKid({ ...newKid, first_name: e.target.value })} required placeholder="Enter first name" />
                  </div>
                  <div className="pd-form-group">
                    <label className="pd-form-label">Last Name <span className="required">*</span></label>
                    <input className="pd-form-input" type="text" value={newKid.last_name} onChange={(e) => setNewKid({ ...newKid, last_name: e.target.value })} required placeholder="Enter last name" />
                  </div>
                  <div className="pd-form-group">
                    <label className="pd-form-label">Date of Birth <span className="required">*</span></label>
                    <input className="pd-form-input" type="date" value={newKid.date_of_birth} onChange={(e) => setNewKid({ ...newKid, date_of_birth: e.target.value })} required />
                    <span className="pd-form-note">* Parent's responsibility to ensure date matches birth certificate</span>
                  </div>
                  <div className="pd-form-group">
                    <label className="pd-form-label">Gender <span className="required">*</span></label>
                    <select className="pd-form-select" value={newKid.gender} onChange={(e) => setNewKid({ ...newKid, gender: e.target.value })} required>
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="pd-form-group">
                    <label className="pd-form-label">Skill Category <span className="required">*</span></label>
                    <select className="pd-form-select" value={newKid.skill_category_id} onChange={(e) => setNewKid({ ...newKid, skill_category_id: e.target.value })} required>
                      <option value="">Select skill category</option>
                      {skillCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </select>
                  </div>
                  <div className="pd-form-group">
                    <label className="pd-form-label">Birth Certificate (Optional)</label>
                    <label className="pd-file-label">
                      <span>📎</span>
                      <span>{birthCertificate ? birthCertificate.name : 'Choose file (optional)'}</span>
                      <input type="file" accept=".jpg,.jpeg,.png,.gif,.pdf" onChange={(e) => setBirthCertificate(e.target.files[0])} style={{ display: 'none' }} />
                    </label>
                    <span className="pd-form-note">Accepted: JPG, PNG, GIF, PDF (Max 5MB)</span>
                  </div>
                </div>
                <div className="pd-form-actions">
                  <button type="submit" className="pd-btn pd-btn-accent">Register Player</button>
                  <button type="button" className="pd-btn pd-btn-secondary" onClick={() => setActiveTab('dashboard')}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
      </div>

      {/* ═══════ AREA DETAIL MODAL ═══════ */}
      {showDetailModal && selectedAreaDetail && (
        <div className="pd-modal-overlay" onClick={closeAreaDetailModal}>
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header">
              <div className="pd-modal-title">
                <span>{getAreaIcon(selectedAreaDetail.area_name)}</span> {selectedAreaDetail.area_name} Details
              </div>
              <button className="pd-modal-close" onClick={closeAreaDetailModal}>×</button>
            </div>
            <div className="pd-modal-body">
              <div className="pd-modal-summary">
                <div className="pd-modal-summary-item"><span className="pd-modal-summary-label">Player</span><span className="pd-modal-summary-value">{selectedAreaDetail.kid_name}</span></div>
                <div className="pd-modal-summary-item"><span className="pd-modal-summary-label">Date</span><span className="pd-modal-summary-value">{formatDate(selectedAreaDetail.assessment_date)}</span></div>
                <div className="pd-modal-summary-item"><span className="pd-modal-summary-label">Area Average</span><span className="pd-modal-summary-value" style={{ color: selectedAreaDetail.avg_score >= 8 ? '#059669' : selectedAreaDetail.avg_score >= 6 ? '#0891b2' : '#d97706' }}>{formatScore(selectedAreaDetail.avg_score)}/10</span></div>
                {selectedAreaDetail.target_score > 0 && <div className="pd-modal-summary-item"><span className="pd-modal-summary-label">Target</span><span className="pd-modal-summary-value">{formatScore(selectedAreaDetail.target_score)}</span></div>}
              </div>
              <div className="pd-modal-questions">
                <h4>Detailed Ratings</h4>
                {selectedAreaDetail.scores && selectedAreaDetail.scores.length > 0 ? (
                  selectedAreaDetail.scores.map((score, i) => (
                    <div key={i} className="pd-question-item">
                      <div className="pd-question-header">
                        <span className="pd-question-name">{score.skill_name}</span>
                        <span className="pd-question-score" style={{ color: score.score >= 8 ? '#059669' : score.score >= 6 ? '#0891b2' : '#d97706' }}>{formatScore(score.score)}/10</span>
                      </div>
                      {score.comments && <div className="pd-question-comments">💬 {score.comments}</div>}
                    </div>
                  ))
                ) : <p style={{ color: '#64748b' }}>No detailed ratings available</p>}
              </div>
              {selectedAreaDetail.area_comments && selectedAreaDetail.area_comments[selectedAreaDetail.area_name] && (
                <div className="pd-modal-area-comments">
                  <h5><span>💬</span> Coach Comments for {selectedAreaDetail.area_name}</h5>
                  <p style={{ margin: 0, color: '#64748b' }}>{selectedAreaDetail.area_comments[selectedAreaDetail.area_name]}</p>
                </div>
              )}
            </div>
            <div className="pd-modal-footer">
              <button className="pd-modal-close-btn" onClick={closeAreaDetailModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Waiver Modal */}
      {showWaiverModal && pendingKidData && (
        <KidRegistrationWaiverModal user={user} kidData={pendingKidData} onComplete={handleWaiverComplete} onCancel={handleWaiverCancel} />
      )}

      {/* Profile Pic Modal */}
      <ProfilePicModal
        isOpen={picModalOpen}
        currentPicUrl={picModalTarget === 'parent' ? resolveProfilePicUrl(profilePicUrl) : resolveProfilePicUrl(selectedPlayerProfile?.profile_pic_url || null)}
        userId={picModalTarget === 'parent' ? user.id : picModalTarget}
        isKid={picModalTarget !== 'parent'}
        parentId={user.id}
        initials={picModalTarget === 'parent'
          ? `${(user.first_name || user.firstName || '?')[0]}${(user.last_name || user.lastName || '?')[0]}`
          : selectedPlayerProfile ? `${(selectedPlayerProfile.first_name || '?')[0]}${(selectedPlayerProfile.last_name || '?')[0]}` : '??'
        }
        onClose={() => setPicModalOpen(false)}
        onSave={(file) => {
          if (picModalTarget === 'parent') {
            handleProfilePicUpload(file);
          } else {
            handleKidProfilePicUpload(file, picModalTarget);
          }
        }}
        uploading={picModalTarget === 'parent' ? uploadingPic : uploadingKidPic}
      />
    </div>
  );
};

export default ParentDashboard;
